import { Button } from '@components/_ui/Button'
import { Datepicker } from '@components/_ui/Datepicker'
import { Grid } from '@components/_ui/Grid'
import { Input } from '@components/_ui/Input'
import { Loader } from '@components/_ui/Loader'
import { MotionWrapper } from '@components/_ui/MotionWrapper'
import { Question } from '@components/_ui/Question'
import { Textarea } from '@components/_ui/Textarea'
import { Autocomplete } from '@components/Autocomplete'
import { quiz } from '@constants/data'
import { useFetch } from '@hooks/useFetch'
import { useQuestionsBySlots } from '@hooks/useQuestionsBySlots'
import { useScrollToNewQuestion } from '@hooks/useScrollToNewQuestion'
import { useSteps } from '@hooks/useSteps'
import { filterSlots } from '@lib/filterSlots'
import { formatDate } from '@lib/formatDate'
import { formatToPrettyDate } from '@lib/formatToPrettyDate'
import { transformFormData } from '@lib/transformFormData'
import { QuestionType, Slots } from '@types'
import { differenceInDays, differenceInMinutes } from 'date-fns'
import { AnimatePresence } from 'motion/react'
import { ReactNode, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import s from './Form.module.scss'
import type { FormProps } from './Form.props'

export function Form({ data }: FormProps) {
  const { trigger: getInitialDay, isLoading: initialLoading } = useFetch('get_initial_day')
  const { trigger: getSlotsByDay, isLoading: slotsLoading } = useFetch('get_slots_by_day')

  const [excludedDates, setExcludedDates] = useState<Date[]>([])
  const [slots, setSlots] = useState<Slots>({})

  const { register, setValue, unregister } = useFormContext()

  const name = useWatch({ name: 'name' })
  const date = useWatch({ name: 'date' })
  const address = useWatch({ name: 'address' })

  const questions = useQuestionsBySlots({ data, date, slots, timeFieldName: 'time' })
  const questionRefs = useScrollToNewQuestion(questions)

  const changeAddressHandler = (value: any) => {
    setValue('address', value?.addressText)
    setValue('address_detail', JSON.stringify(value))
  }

  const selectDate = (selectedDate: Date | null) => {
    const formattedDate = selectedDate ? formatDate(selectedDate) : null

    setValue('date', formattedDate)
    setValue(data?.[1]?.db_field_name, null)
    unregister(data?.[1]?.db_field_name)

    if (formattedDate && !slots?.[formattedDate]) {
      getSlotsByDay({ date: formattedDate })
        .then((res: { data: { data: Slots } }) => {
          const { enabledSlots, disabledDates } = filterSlots(res.data.data)
          setSlots((prev) => ({ ...prev, ...enabledSlots }))
          setExcludedDates((prev) => [...prev, ...disabledDates])
        })
    }
  }

  useEffect(() => {
    getInitialDay()
      .then((res: { data: { data: Slots } }) => {
        const { enabledSlots, disabledDates } = filterSlots(res.data.data)
        const firstAvailableDate = Object.keys(enabledSlots)?.filter((date) => differenceInDays(date, quiz.data.first_availability.date) >= 0)?.[0]
        setValue('date', firstAvailableDate)
        setSlots((prev) => ({
          ...prev,
          ...Object.fromEntries(Object.entries(enabledSlots)
            ?.map(([date, values]) =>
              date === quiz.data.first_availability.date
                ? [
                  date,
                  values?.filter((slot) => {
                    const slotStartTime = slot.time.split(' - ')[0]
                    const availabilityStartTime = quiz.data.first_availability.time.split(' - ')[0]

                    return differenceInMinutes(`${date}T${slotStartTime}`, `${quiz.data.first_availability.date}T${availabilityStartTime}`) >= 0
                  })
                ]
                : [date, values]
            )
          )
        }))
        setExcludedDates((prev) => [...prev, ...disabledDates])
      })
  }, [])

  return (
    <div className={s.form}>
      <AnimatePresence initial={false}>
        {questions?.length && questions.map((question, index) => {
          const Wrapper = question.question_type === 'slots' ? MotionWrapper : 'div'

          return (
            <Wrapper
              key={question.db_field_name}
              ref={(el) => (questionRefs.current[question.db_field_name] = el)}
              withIndent={question.question_type === 'slots'}
              className={s.item}
            >
              <Question
                question={question.question?.replace('[name]', name)}
                info={question?.explanation?.content}
                index={index + 1}
                className={s.question}
              />

              {({
                calendar: (
                  <div className={s.datepicker}>
                    <Datepicker
                      selected={date ? new Date(`${date}T00:00:00`) : null}
                      onChange={selectDate}
                      minDate={new Date(`${quiz.data.first_availability.date}T00:00:00`)}
                      excludeDates={excludedDates}
                    />

                    {(initialLoading || slotsLoading) && <Loader className={s.loader} />}
                  </div>
                ),
                slots: (
                  <Grid
                    options={question.options}
                    {...register(question.db_field_name)}
                  />
                ),
                formFields: (
                  <div className={s.wrapper}>
                    <Autocomplete
                      initialValue={address}
                      onChange={changeAddressHandler}
                    />

                    <Input
                      {...register('unit')}
                      placeholder="Apartment (Unit)"
                    />
                  </div>
                ),
                textArea: (
                  <Textarea
                    {...register(question.db_field_name)}
                    placeholder="Type your notes here"
                  />
                )
              } as Record<QuestionType, ReactNode>)[question.question_type]}
            </Wrapper>
          )
        })}
      </AnimatePresence>
    </div>
  )
}

function FormButton({ data }: FormProps) {
  const { handleSubmit } = useFormContext()
  const { trigger, isLoading } = useFetch()
  const { stepsHistory, setStepsHistory, setCurrentStep } = useSteps()

  const date = useWatch({ name: 'date' })
  let formattedDate = formatToPrettyDate(date)
  formattedDate = formattedDate.slice(formattedDate.indexOf(', ') + 2)

  const time = useWatch({ name: 'time' })
  const timeObj = time ? JSON.parse(time) as Slots[string][number] : null

  const address = useWatch({ name: 'address' })
  const addressDetail = useWatch({ name: 'address_detail' })
  const unit = useWatch({ name: 'unit' })

  const disabled = !time || !address || !addressDetail

  const nextHandler = handleSubmit((formData) => {
    if (disabled) return

    const nextStep = 7
    const newHistory = [...stepsHistory, 7]

    const transformedFormData = transformFormData(data, formData)

    trigger({
      ...transformedFormData,
      data: {
        ...transformedFormData.data,
        address_detail: addressDetail,
        unit
      },
      current_screen: nextStep,
      history: newHistory
    }).then(() => {
      (window as any)?.fbq?.('track', 'Purchase')

      setCurrentStep(nextStep)
      setStepsHistory(newHistory)
    })
  })

  return (
    <Button
      onClick={nextHandler}
      disabled={isLoading || disabled}
    >
      {isLoading
        ? <Loader />
        : `Confirm Cleaning${!!formattedDate && !!timeObj ? ` - ${formattedDate}, ${timeObj.time_display}` : ''}`
      }
    </Button>
  )
}

Form.Button = FormButton
